import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  Mail, 
  MessageSquare, 
  Clock,
  Save,
  Check,
  AlertCircle
} from 'lucide-react';
import { communicationPreferencesService, auditService } from '../../services';
import { CommunicationPreferences, UpdateCommunicationPreferences } from '../../types/messaging';
import { useAuthStore } from '../../stores/authStore';

interface NotificationPreferencesProps {
  className?: string;
  showTitle?: boolean;
}

export const NotificationPreferences: React.FC<NotificationPreferencesProps> = ({
  className = '',
  showTitle = true
}) => {
  const { user } = useAuthStore();
  const [preferences, setPreferences] = useState<CommunicationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    // Email preferences
    email_enabled: true,
    email_bookings: true,
    email_quotes: true,
    email_messages: true,
    email_marketing: false,
    // WhatsApp preferences
    whatsapp_enabled: false,
    whatsapp_number: '',
    whatsapp_bookings: false,
    whatsapp_quotes: false,
    whatsapp_messages: false,
    // System notification preferences
    system_enabled: true,
    system_bookings: true,
    system_quotes: true,
    system_messages: true,
    // Timing preferences
    quiet_hours_start: '22:00',
    quiet_hours_end: '07:00',
    timezone: 'Africa/Johannesburg'
  });

  useEffect(() => {
    if (user?.id) {
      loadPreferences();
    }
  }, [user?.id]);

  const loadPreferences = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const response = await communicationPreferencesService.getPreferences(user.id);
      
      if (response.success && response.data) {
        setPreferences(response.data);
        setFormData({
          email_enabled: response.data.email_enabled,
          email_bookings: response.data.email_bookings,
          email_quotes: response.data.email_quotes,
          email_messages: response.data.email_messages,
          email_marketing: response.data.email_marketing,
          whatsapp_enabled: response.data.whatsapp_enabled,
          whatsapp_number: response.data.whatsapp_number || '',
          whatsapp_bookings: response.data.whatsapp_bookings,
          whatsapp_quotes: response.data.whatsapp_quotes,
          whatsapp_messages: response.data.whatsapp_messages,
          system_enabled: response.data.system_enabled,
          system_bookings: response.data.system_bookings,
          system_quotes: response.data.system_quotes,
          system_messages: response.data.system_messages,
          quiet_hours_start: response.data.quiet_hours_start,
          quiet_hours_end: response.data.quiet_hours_end,
          timezone: response.data.timezone
        });
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
      setError('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user?.id) return;

    try {
      setSaving(true);
      setError(null);

      let response;
      
      if (preferences) {
        // Update existing preferences
        const updateData: UpdateCommunicationPreferences = formData;
        response = await communicationPreferencesService.updatePreferences(user.id, updateData);
      } else {
        // Create default preferences (this will be handled by getPreferences if none exist)
        response = await communicationPreferencesService.createDefaultPreferences(user.id);
        
        // Then update with user's preferred settings
        if (response.success) {
          const updateData: UpdateCommunicationPreferences = formData;
          response = await communicationPreferencesService.updatePreferences(user.id, updateData);
        }
      }

      if (response.success && response.data) {
        setPreferences(response.data);
        setSaveSuccess(true);
        
        // Log the action
        await auditService.logActivity({
          user_id: user.id,
          action: preferences ? 'update' : 'create',
          entity_type: 'preference',
          entity_id: response.data.id,
          old_values: preferences ? {
            email_enabled: preferences.email_enabled,
            whatsapp_enabled: preferences.whatsapp_enabled,
            system_enabled: preferences.system_enabled
          } : undefined,
          new_values: {
            email_enabled: formData.email_enabled,
            whatsapp_enabled: formData.whatsapp_enabled,
            system_enabled: formData.system_enabled
          }
        });

        // Clear success message after 3 seconds
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError(response.error?.message || 'Failed to save preferences');
      }
    } catch (error) {
      console.error('Failed to save preferences:', error);
      setError('An error occurred while saving preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-32 ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      {showTitle && (
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Bell className="w-5 h-5 mr-2" />
            Notification Preferences
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Control how and when you receive notifications
          </p>
        </div>
      )}

      <div className="p-6 space-y-8">
        {/* Email Notifications */}
        <div>
          <div className="flex items-center mb-4">
            <Mail className="w-5 h-5 text-blue-600 mr-2" />
            <h4 className="text-lg font-medium text-gray-900">Email Notifications</h4>
          </div>
          
          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.email_enabled}
                onChange={(e) => handleInputChange('email_enabled', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-3 text-sm text-gray-700">
                Enable email notifications
              </span>
            </label>

            {formData.email_enabled && (
              <div className="ml-7 space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.email_bookings}
                    onChange={(e) => handleInputChange('email_bookings', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm text-gray-600">
                    Booking confirmations and updates
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.email_quotes}
                    onChange={(e) => handleInputChange('email_quotes', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm text-gray-600">
                    Quote requests and responses
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.email_messages}
                    onChange={(e) => handleInputChange('email_messages', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm text-gray-600">
                    New messages and conversations
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.email_marketing}
                    onChange={(e) => handleInputChange('email_marketing', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm text-gray-600">
                    Marketing and promotional emails
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* WhatsApp Notifications */}
        <div>
          <div className="flex items-center mb-4">
            <MessageSquare className="w-5 h-5 text-green-600 mr-2" />
            <h4 className="text-lg font-medium text-gray-900">WhatsApp Notifications</h4>
          </div>
          
          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.whatsapp_enabled}
                onChange={(e) => handleInputChange('whatsapp_enabled', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-3 text-sm text-gray-700">
                Enable WhatsApp notifications
              </span>
            </label>

            {formData.whatsapp_enabled && (
              <div className="ml-7 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    WhatsApp Number
                  </label>
                  <input
                    type="tel"
                    placeholder="+27 XX XXX XXXX"
                    value={formData.whatsapp_number}
                    onChange={(e) => handleInputChange('whatsapp_number', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.whatsapp_bookings}
                    onChange={(e) => handleInputChange('whatsapp_bookings', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm text-gray-600">
                    Booking confirmations and updates
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.whatsapp_quotes}
                    onChange={(e) => handleInputChange('whatsapp_quotes', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm text-gray-600">
                    Quote requests and responses
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.whatsapp_messages}
                    onChange={(e) => handleInputChange('whatsapp_messages', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm text-gray-600">
                    New messages and conversations
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* System Notifications */}
        <div>
          <div className="flex items-center mb-4">
            <Bell className="w-5 h-5 text-yellow-600 mr-2" />
            <h4 className="text-lg font-medium text-gray-900">In-App Notifications</h4>
          </div>
          
          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.system_enabled}
                onChange={(e) => handleInputChange('system_enabled', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-3 text-sm text-gray-700">
                Enable in-app notifications
              </span>
            </label>

            {formData.system_enabled && (
              <div className="ml-7 space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.system_bookings}
                    onChange={(e) => handleInputChange('system_bookings', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm text-gray-600">
                    Booking notifications
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.system_quotes}
                    onChange={(e) => handleInputChange('system_quotes', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm text-gray-600">
                    Quote notifications
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.system_messages}
                    onChange={(e) => handleInputChange('system_messages', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm text-gray-600">
                    Message notifications
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Quiet Hours */}
        <div>
          <div className="flex items-center mb-4">
            <Clock className="w-5 h-5 text-purple-600 mr-2" />
            <h4 className="text-lg font-medium text-gray-900">Quiet Hours</h4>
          </div>
          
          <p className="text-sm text-gray-600 mb-4">
            Set times when you don't want to receive notifications
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time
              </label>
              <input
                type="time"
                value={formData.quiet_hours_start}
                onChange={(e) => handleInputChange('quiet_hours_start', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Time
              </label>
              <input
                type="time"
                value={formData.quiet_hours_end}
                onChange={(e) => handleInputChange('quiet_hours_end', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        {/* Success Message */}
        {saveSuccess && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
            <Check className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-sm text-green-700">
              Notification preferences saved successfully!
            </span>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-6 py-2 rounded-lg font-medium flex items-center space-x-2 ${
              saving
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-primary-600 text-white hover:bg-primary-700'
            }`}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save Preferences</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};